import type {
  CreateInvoiceLineItemInput,
  Product,
  User,
} from 'payments-api/model';
import type { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useCallback, useState } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import {
  currencyCodes,
  currencyPrecision as currencyPrecisionMap,
} from '@fragment/types/currencyCodes';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Icon } from '@fragment/ui/src/components/Icon';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { CurrencyField } from '@fragment/ui/src/components/Payments/CurrencyField';
import {
  ItemTypeCell,
  LayoutCell,
  LayoutRow,
} from '@fragment/ui/src/components/Payments/LayoutRow';

import { getUsersForProductByItemType } from './invoiceItemUtils';

export type ItemTableItem = CreateInvoiceLineItemInput & {
  id: string;
  paid?: number;
};

const ItemRow = ({
  item,
  index,
  onRemove,
  onEdit,
}: {
  item: ItemTableItem;
  index: number;
  onRemove?: () => void;
  onEdit?: () => void;
}) => {
  const amountNum = Number(item.amount);
  const precision =
    currencyPrecisionMap[item.currencyCode as CurrencyCode] || 2;
  return (
    <LayoutRow height="medium" data-testid={`item-row-${index}`}>
      <ItemTypeCell itemType={item.type} />
      <LayoutCell width="full" border="none" align="top">
        <span className="text-main truncate w-full text-left">
          {item.product_id || '—'}
        </span>
        <span className="text-main text-md w-full min-h-[1lh]">
          {item.user_id ? `${item.user_id}` : ''}
        </span>
        <span className="text-main-500 text-md w-full truncate text-left min-h-[1lh]">
          {item.description || ''}
        </span>
      </LayoutCell>
      {item.paid !== undefined && !onEdit && (
        <LayoutCell width="full" border="none" align="top" justify="end">
          <CurrencyField
            amount={Number(item.paid)}
            currencyPrecision={precision}
            currencyCode={item.currencyCode as CurrencyCode}
            textSize="md"
            align="right"
            width="full"
          />
        </LayoutCell>
      )}
      <LayoutCell width="full" border="none" align="top" justify="end">
        <CurrencyField
          amount={amountNum}
          currencyPrecision={precision}
          currencyCode={item.currencyCode as CurrencyCode}
          textSize="md"
          align="right"
          width="full"
        />
        {onEdit && (
          <TextButton onClick={onEdit} data-testid={`item-row-${index}-edit`}>
            Edit
          </TextButton>
        )}
        {onRemove && (
          <TextButton
            onClick={onRemove}
            data-testid={`item-row-${index}-remove`}
            className="text-main-500 hover:text-main text-md w-fit text-left"
            icon={<Icon type="trash" size="md" primaryColor="gray" />}
            hoverIcon={<Icon type="trash" size="md" primaryColor="black" />}
          />
        )}
      </LayoutCell>
    </LayoutRow>
  );
};

const DraftItemRow = ({
  products,
  users,
  initialValues,
  onSubmit,
  onUpdate,
  onCancel,
}: {
  products: Product[];
  users: User[];
  initialValues: ItemTableItem | null;
  onSubmit: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdate: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onCancel: () => void;
}) => {
  const [itemType, setItemType] = useState<InvoiceLineItemType>(
    initialValues?.type || 'payin'
  );
  const isEdit = initialValues !== null;
  const [productCode, setProductCode] = useState(
    isEdit ? initialValues.product_id : ''
  );
  const [userId, setUserId] = useState(isEdit ? initialValues.user_id : '');
  const [description, setDescription] = useState(
    isEdit ? initialValues.description : ''
  );
  const [amount, setAmount] = useState(isEdit ? initialValues.amount : '');
  const [currencyCode, setCurrencyCode] = useState<string>(
    isEdit ? initialValues.currencyCode : 'USD'
  );

  const handleProductChange = useCallback((code: string) => {
    setProductCode(code);
    setUserId('');
  }, []);

  const productOptions = products
    .filter((p) =>
      itemType === 'payin'
        ? p.paid_by_roles.length > 0
        : p.paid_to_roles.length > 0
    )
    .map((p) => ({
      value: p.code,
      label: `${p.code} - ${p.description}`,
    }));

  const userOptions = getUsersForProductByItemType(
    productCode,
    itemType,
    products,
    users
  );

  const handleSubmit = useCallback(() => {
    const item: Omit<ItemTableItem, 'id'> = {
      type: itemType,
      product_id: productCode,
      user_id: userId,
      amount,
      currencyCode: currencyCode as CurrencyCode,
      description,
    };
    if (isEdit) {
      onUpdate(initialValues.id, item);
    } else {
      onSubmit(item);
    }
    onCancel();
  }, [
    itemType,
    productCode,
    userId,
    currencyCode,
    description,
    amount,
    isEdit,
    initialValues,
    onSubmit,
    onUpdate,
    onCancel,
  ]);

  const canSubmit =
    productCode.trim() !== '' &&
    userId.trim() !== '' &&
    amount.trim() !== '' &&
    !Number.isNaN(Number(amount));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-y-f1 w-full border-main border-[1px] border-t-0 p-f1 min-h-f12"
    >
      <div className="flex flex-row gap-x-f1 w-full">
        <span className="text-main text-md min-w-f6">Type</span>
        <Dropdown
          options={[
            { value: 'payin', label: 'Payin' },
            { value: 'payout', label: 'Payout' },
          ]}
          onChange={(value) => {
            setItemType(value as InvoiceLineItemType);
            setProductCode('');
            setUserId('');
          }}
          displayValue={itemType}
          size="md"
          width="w-full"
          height="h-fit"
          align="left"
          data-testid="draft-item-type-dropdown"
        />
      </div>
      <div className="flex flex-row gap-x-f1 w-full">
        <span className="text-main text-md min-w-f6">Product</span>
        <Dropdown
          options={productOptions}
          onChange={handleProductChange}
          displayValue={productCode || 'Select a product'}
          size="md"
          width="w-full"
          height="h-fit"
          align="left"
          data-testid="draft-item-product-dropdown"
        />
      </div>
      <div className="flex flex-row gap-x-f1 w-full">
        <span className="text-main text-md min-w-f6">User</span>
        <Dropdown
          options={userOptions}
          onChange={setUserId}
          displayValue={userId || 'Select a user'}
          size="md"
          width="w-full"
          height="h-fit"
          align="left"
          data-testid="draft-item-user-dropdown"
        />
      </div>
      <TextInput
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        size="md"
        width="w-full"
        height="h-fit"
        padding="p-0"
        align="left"
        data-testid="draft-item-description-input"
      />
      <div className="flex flex-row gap-x-f1 w-full">
        <span className="text-main text-md min-w-f6">Amount</span>
        <TextInput
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          size="md"
          width="w-full"
          height="h-fit"
          padding="p-0"
          align="left"
          data-testid="draft-item-amount-input"
        />
      </div>
      <div className="flex flex-row gap-x-f1 w-full">
        <span className="text-main text-md min-w-f6">Currency</span>
        <Dropdown
          options={currencyCodes
            .filter((code) => code !== 'LOGICAL' && code !== 'CUSTOM')
            .map((code) => ({
              value: code,
              label: code,
            }))}
          onChange={setCurrencyCode}
          displayValue={currencyCode}
          size="md"
          width="w-full"
          height="h-fit"
          align="left"
          data-testid="draft-item-currency-dropdown"
        />
      </div>
      <FormButtons
        type="secondary"
        cancelButtonLabel="Cancel"
        onCancel={onCancel}
        submitButtonLabel={isEdit ? 'Save' : 'Add+'}
        disabled={!canSubmit}
        submitButtonTestId="draft-item-submit-button"
        cancelButtonTestId="draft-item-cancel-button"
      />
    </form>
  );
};

const NewItemRow = ({ onAdd }: { onAdd: () => void }) => (
  <button
    type="button"
    className="flex w-full h-full p-f1 bg-main-200 border-main border-[1px] border-t-0  hover:text-main hover:bg-negative text-center items-center justify-center"
    onClick={onAdd}
    aria-label="Add new item"
    data-testid="add-new-item-button"
  >
    Add New Item+
  </button>
);

type ItemTableProps = {
  items: ItemTableItem[];
  products: Product[];
  users: User[];
  onAddItem?: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdateItem?: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onRemoveItem?: (id: string) => void;
  'data-testid'?: string;
};

export const ItemTable = ({
  items,
  products,
  users,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  'data-testid': dataTestId,
}: ItemTableProps): JSX.Element => {
  const [showDraftItem, setShowDraftItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const editingItem =
    editingItemId !== null
      ? items.find((i) => i.id === editingItemId) ?? null
      : null;

  const handleAddItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      onAddItem?.(item);
      setShowDraftItem(false);
      setEditingItemId(null);
    },
    [onAddItem]
  );

  const handleUpdateItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      onUpdateItem?.(id, item);
      setShowDraftItem(false);
      setEditingItemId(null);
    },
    [onUpdateItem]
  );

  const handleCancelDraft = useCallback(() => {
    setShowDraftItem(false);
    setEditingItemId(null);
  }, []);

  const handleStartEdit = useCallback((id: string) => {
    setEditingItemId(id);
    setShowDraftItem(true);
  }, []);

  const handleStartAdd = useCallback(() => {
    setEditingItemId(null);
    setShowDraftItem(true);
  }, []);

  const itemsToShow = items
    .filter((item) => item.id !== editingItemId)
    .sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'payin' ? -1 : 1;
    });

  return (
    <div data-testid={dataTestId} className="flex flex-col h-fit w-full">
      {!showDraftItem && onAddItem && <NewItemRow onAdd={handleStartAdd} />}
      {showDraftItem && (onAddItem || onUpdateItem) && (
        <DraftItemRow
          key={editingItemId ?? 'add'}
          products={products}
          users={users}
          initialValues={editingItem}
          onSubmit={handleAddItem}
          onUpdate={handleUpdateItem}
          onCancel={handleCancelDraft}
        />
      )}
      {itemsToShow.map((item, index) => (
        <ItemRow
          key={item.id}
          item={item}
          index={index}
          onRemove={onRemoveItem ? () => onRemoveItem(item.id) : undefined}
          onEdit={onUpdateItem ? () => handleStartEdit(item.id) : undefined}
        />
      ))}
    </div>
  );
};
